import { ExpandMoreOutlined } from "@mui/icons-material";
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Link,
    Typography,
} from "@mui/material";
import ReactMarkdown from "react-markdown";
import { Link as RouterLink } from "react-router-dom";

import { DocumentPill, isDocumentHref } from "./DocumentPill";
import { openInNewTab } from "./embed-utils";

import type { WidgetProps } from "./types";

type FaqItem = WidgetProps<"faq">["widget"]["props"]["faqs"][number];

// FAQ answers are markdown: paragraphs, bold, numbered steps and links.
// Links (2026-09-04): an absolute or external href opens in a new tab (the
// /program-docs PDFs, Amazon's portals); a relative href ("../map") is a
// page in this room and resolves against the current URL, so the same
// canonical copy works on the gated and the preview paths. The new tab is
// opened from script (openInNewTab, 2026-09-05) with target="_blank" kept as
// the fallback, because some extensions and in-app browsers strip the
// attribute and would navigate the room away (same fix as the brand wall).
export const answerMarkdownComponents = {
    p: ({ children }: { children?: React.ReactNode }) => (
        <Typography
            variant="body2"
            color="text.secondary"
            sx={{ lineHeight: 1.65, mb: 1.25, "&:last-child": { mb: 0 } }}
        >
            {children}
        </Typography>
    ),
    strong: ({ children }: { children?: React.ReactNode }) => (
        <Box component="strong" sx={{ fontWeight: 700, color: "text.primary" }}>
            {children}
        </Box>
    ),
    a: ({ children, href = "" }: { children?: React.ReactNode; href?: string }) =>
        isDocumentHref(href) ? (
            <DocumentPill href={href} label={children} inline />
        ) : /^(https?:)?\//.test(href) ? (
            <Link
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                onClick={openInNewTab(href)}
            >
                {children}
            </Link>
        ) : (
            <Link component={RouterLink} to={href} relative="path">
                {children}
            </Link>
        ),
    ol: ({ children }: { children?: React.ReactNode }) => (
        <Box component="ol" sx={{ pl: 3, mt: 0, mb: 1.25 }}>
            {children}
        </Box>
    ),
    ul: ({ children }: { children?: React.ReactNode }) => (
        <Box component="ul" sx={{ pl: 3, mt: 0, mb: 1.25 }}>
            {children}
        </Box>
    ),
    li: ({ children }: { children?: React.ReactNode }) => (
        <Typography
            component="li"
            variant="body2"
            color="text.secondary"
            sx={{ lineHeight: 1.65, mb: 0.5 }}
        >
            {children}
        </Typography>
    ),
};

// Consecutive items sharing a `section` form one group. Items with no
// section (legacy stored FAQs) form an unheaded group.
function groupBySection(faqs: FaqItem[]): { section?: string; items: FaqItem[] }[] {
    const groups: { section?: string; items: FaqItem[] }[] = [];
    for (const faq of faqs) {
        const last = groups[groups.length - 1];
        if (last && last.section === faq.section) last.items.push(faq);
        else groups.push({ section: faq.section, items: [faq] });
    }
    return groups;
}

// Two-tone brand icons (navy strokes, orange accents) served from
// public/brand/icons/faq/, the same <img> pattern the public site uses for
// its Nucleo icons. Each question row gets a 24px mark (2026-09-05, trialled
// on hush-crib behind a showIcons flag, then made the default the same day).
// Section headings carry no icon (the tiles shipped and were removed on
// Bill's request).
function FaqIcon({ name, size }: { name: string; size: number }) {
    return (
        <Box
            component="img"
            src={`/brand/icons/faq/${name}.svg`}
            alt=""
            aria-hidden
            sx={{ width: size, height: size, flexShrink: 0, display: "block" }}
        />
    );
}

export function FAQ({ widget }: WidgetProps<"faq">) {
    const { title, description, faqs } = widget.props;

    return (
        <Box>
            {title && (
                <Typography variant="h4" sx={{ mb: description ? 0.5 : 2 }}>
                    {title}
                </Typography>
            )}
            {description && (
                <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 3, maxWidth: 720 }}
                >
                    {description}
                </Typography>
            )}
            {groupBySection(faqs).map((group, gi) => (
                <Box key={group.section ?? gi} sx={{ mb: 4, "&:last-child": { mb: 0 } }}>
                    {group.section && (
                        <Typography variant="h4" sx={{ mb: 1.5 }}>
                            {group.section}
                        </Typography>
                    )}
                    <Box
                        sx={{
                            // 90% of the content column (Bill, 2026-09-05:
                            // first 86%, then 4 points wider); full width on a
                            // phone so both margins match (Bill, 2026-09-08).
                            width: { xs: "100%", md: "90%" },
                            border: 1,
                            borderColor: "divider",
                            borderRadius: "10px",
                            overflow: "hidden",
                            bgcolor: "background.paper",
                        }}
                    >
                        {group.items.map((faq, idx) => (
                            <Accordion
                                key={`${faq.question}-${idx}`}
                                elevation={0}
                                disableGutters
                                sx={{
                                    "&:before": { display: "none" },
                                    borderBottom: idx < group.items.length - 1 ? 1 : 0,
                                    borderColor: "divider",
                                    "&.Mui-expanded": { my: 0 },
                                }}
                            >
                                <AccordionSummary
                                    expandIcon={<ExpandMoreOutlined />}
                                    sx={{
                                        px: 3,
                                        py: 1.5,
                                        "&:hover": { bgcolor: "grey.50" },
                                        "& .MuiAccordionSummary-content": {
                                            my: 1.25,
                                            alignItems: "center",
                                            gap: 2,
                                        },
                                    }}
                                >
                                    {faq.icon && <FaqIcon name={faq.icon} size={24} />}
                                    <Typography
                                        variant="body1"
                                        sx={{ fontWeight: 600, color: "text.primary" }}
                                    >
                                        {faq.question}
                                    </Typography>
                                </AccordionSummary>
                                <AccordionDetails sx={{ px: 3, pt: 0, pb: 3 }}>
                                    <ReactMarkdown components={answerMarkdownComponents}>
                                        {faq.answer}
                                    </ReactMarkdown>
                                </AccordionDetails>
                            </Accordion>
                        ))}
                    </Box>
                </Box>
            ))}
        </Box>
    );
}
